
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Utensils } from 'lucide-react';
import { GeminiLiveClient } from './services/geminiLiveService';
import { correctTypos } from './services/moleculeService'; 
import { runFullAnalysis, analyzeDietaryProfile } from './services/analysisService';
import { getStaticRecipe } from './services/recipeDatabase'; 
import { scanProduct } from './services/openFoodFactsService'; 
import { isBaseIngredient, foods as dbFoodsList } from './database/food-database'; 
import { normalizeSymptom, normalizeFood, getFoodNameById } from './constants/autocompleteData'; 
import { getDecomposition } from './database/decomposition-map';
import { FoodItem, LogFoodArgs, ConnectionStatus, MealSession, SymptomSession, AppMode, LogSymptomArgs, SymptomItem, UserSettings, Language, AnalysisResult, MealTypeKey, FavoriteMeal, SmartSuggestionItem, CustomRecipe, PantryItem, WaterLog, ActivityLog, VoiceCommandArgs, ScaleLog, Medication, MedicationLog, StorageLocation, PantryCategory } from './types';
import { t } from './locales';
import MicButton from './components/MicButton';
import FoodList from './components/FoodList';
import SymptomList from './components/SymptomList';
import Stats from './components/Stats';
import HomeView from './components/HomeView';
import StatsView from './components/StatsView'; 
import { SettingsView } from './components/SettingsView';
import LoginView from './components/LoginView';
import DaySummary from './components/DaySummary';
import AddMethodModal from './components/AddMethodModal';
import ManualEntryModal from './components/ManualEntryModal';
import { analyzeFoodImage } from './services/imageAnalysisService';
import CameraCaptureModal from './components/CameraCaptureModal';
import BarcodeScannerModal from './components/BarcodeScannerModal'; 
import Wizard from './components/Wizard';
import FavoriteNameModal from './components/FavoriteNameModal';
import WeeklyCalendar from './components/WeeklyCalendar';
import MonthlyCalendar from './components/MonthlyCalendar'; 
import KitchenModal from './components/KitchenModal'; 
import WaterModal from './components/WaterModal';
import ActivityModal from './components/ActivityModal'; 
import HealthModal from './components/HealthModal'; 
import ScaleModal from './components/ScaleModal'; 
import PharmacyModal from './components/PharmacyModal';
import DonationModal from './components/DonationModal';
import SaveProductModal from './components/SaveProductModal';
import ConfirmationModal from './components/ConfirmationModal';
import { Header } from './components/Header'; 
import { parseNaturalLanguageInput, ParsedItem } from './services/textParsingService';

const App: React.FC = () => {
  // --- Auth State ---
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  
  // --- Settings State ---
  const [settings, setSettings] = useState<UserSettings>({
    language: 'en',
    theme: 'light',
    density: 'standard', 
    notificationsEnabled: false,
    maxFavoritesToShow: 2, 
    maxSmartSuggestions: 20, 
    waterGoal: 2000, 
    standardWaterVolume: 130, 
    weight: 93, 
    gender: 'male',
    height: 193,
    age: 43,
    reminders: [
        { id: 'breakfast', label: 'Breakfast', time: '09:00', enabled: true },
        { id: 'lunch', label: 'Lunch', time: '14:00', enabled: true },
        { id: 'dinner', label: 'Dinner', time: '19:00', enabled: true },
    ]
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showKitchen, setShowKitchen] = useState(false); 
  const [showWaterModal, setShowWaterModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false); 
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [showScaleModal, setShowScaleModal] = useState(false);
  const [showPharmacyModal, setShowPharmacyModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);

  // --- Data State ---
  const [sessions, setSessions] = useState<MealSession[]>([]);
  const [symptomSessions, setSymptomSessions] = useState<SymptomSession[]>([]);
  const [favorites, setFavorites] = useState<FavoriteMeal[]>([]);
  const [customRecipes, setCustomRecipes] = useState<CustomRecipe[]>([]); 
  const [pantryItems, setPantryItems] = useState<PantryItem[]>([]); 

  const sessionsRef = useRef(sessions);
  const symptomSessionsRef = useRef(symptomSessions);
  const customRecipesRef = useRef(customRecipes);
  const pantryItemsRef = useRef(pantryItems);
  const [waterLogs, setWaterLogs] = useState<WaterLog[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]); 
  const [scaleLogs, setScaleLogs] = useState<ScaleLog[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [medicationLogs, setMedicationLogs] = useState<MedicationLog[]>([]);

  // --- Navigation & Calendar State ---
  const [currentViewIndex, setCurrentViewIndex] = useState(0); 
  const [selectedDate, setSelectedDate] = useState<Date>(new Date()); 
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false); 

  // --- Derived Data for UI ---
  const foodHistory = useMemo(() => {
    return sessions.flatMap(s => s.items.map(i => i.name));
  }, [sessions]);

  const symptomHistory = useMemo(() => {
    return symptomSessions.flatMap(s => s.items.map(i => i.symptom));
  }, [symptomSessions]);

  const mealSuggestions = useMemo((): SmartSuggestionItem[] => {
    // ... existing logic ...
    const items: SmartSuggestionItem[] = [];
    const usedLabels = new Set<string>();
    const now = new Date();
    const currentHour = now.getHours();

    const getHourDistance = (h1: number, h2: number) => {
        const diff = Math.abs(h1 - h2);
        return Math.min(diff, 24 - diff);
    };

    const sortedFavorites = [...favorites].sort((a, b) => {
        const getMinutes = (timeStr?: string) => {
             if (!timeStr) return -1;
             const [h, m] = timeStr.split(':').map(Number);
             return h * 60 + m;
        };
        const currentMinutes = currentHour * 60 + now.getMinutes();
        
        const timeA = getMinutes(a.usualTime);
        const timeB = getMinutes(b.usualTime);
        
        if (timeA >= 0 && timeB >= 0) {
             let diffA = Math.abs(currentMinutes - timeA);
             if (diffA > 720) diffA = 1440 - diffA;
             let diffB = Math.abs(currentMinutes - timeB);
             if (diffB > 720) diffB = 1440 - diffB;
             return diffA - diffB;
        }
        if (timeA >= 0) {
             let diff = Math.abs(currentMinutes - timeA);
             if (diff > 720) diff = 1440 - diff;
             if (diff < 180) return -1; 
        }
        if (timeB >= 0) {
             let diff = Math.abs(currentMinutes - timeB);
             if (diff > 720) diff = 1440 - diff;
             if (diff < 180) return 1;
        }
        return 0;
    });

    sortedFavorites.slice(0, 3).forEach(fav => {
      items.push({ type: 'favorite', label: fav.name, data: fav });
      usedLabels.add(fav.name.toLowerCase());
    });

    const frequencyMap: Record<string, number> = {};
    const TIME_WINDOW = 4;

    sessions.forEach(session => {
        const sessionDate = new Date(session.timestamp);
        const sessionHour = sessionDate.getHours();
        
        if (getHourDistance(currentHour, sessionHour) <= TIME_WINDOW) {
            session.items.forEach(item => {
                const key = item.name.trim();
                const lowerKey = key.toLowerCase();
                if (!usedLabels.has(lowerKey)) {
                    frequencyMap[key] = (frequencyMap[key] || 0) + 1;
                }
            });
        }
    });

    const sortedHistory = Object.entries(frequencyMap)
        .sort(([, countA], [, countB]) => countB - countA)
        .map(([name]) => name);

    sortedHistory.forEach(name => {
        if (!usedLabels.has(name.toLowerCase())) {
            items.push({ type: 'item', label: name, data: name });
            usedLabels.add(name.toLowerCase());
        }
    });

    let defaults: string[] = [];
    const isMorning = currentHour >= 5 && currentHour < 11;
    const isLunch = currentHour >= 11 && currentHour < 15;
    const isDinner = currentHour >= 18 || currentHour < 4;

    if (settings.language === 'fr') {
        if (isMorning) defaults = ["Café", "Tartine", "Yaourt", "Banane", "Oeuf", "Jus d'orange"];
        else if (isLunch) defaults = ["Salade", "Sandwich", "Poulet", "Riz", "Pâtes", "Pomme"];
        else if (isDinner) defaults = ["Soupe", "Poisson", "Légumes", "Yaourt", "Tisane"];
        else defaults = ["Pomme", "Yaourt", "Chocolat", "Amandes", "Biscuit"];
    } else {
        if (isMorning) defaults = ["Coffee", "Toast", "Yogurt", "Banana", "Egg", "Oatmeal"];
        else if (isLunch) defaults = ["Salad", "Sandwich", "Chicken", "Rice", "Pasta", "Apple"];
        else if (isDinner) defaults = ["Soup", "Fish", "Vegetables", "Yogurt", "Tea"];
        else defaults = ["Apple", "Yogurt", "Chocolate", "Almonds", "Cookie"];
    }
    
    defaults.forEach(d => {
      if (items.length < settings.maxSmartSuggestions && !usedLabels.has(d.toLowerCase())) {
        items.push({ type: 'item', label: d, data: d });
        usedLabels.add(d.toLowerCase());
      }
    });

    return items.slice(0, settings.maxSmartSuggestions);
  }, [favorites, sessions, settings.language, settings.maxSmartSuggestions]);

  const symptomSuggestions = useMemo((): SmartSuggestionItem[] => {
    const items: SmartSuggestionItem[] = [];
    const defaults = settings.language === 'fr'
      ? ["Mal de tête", "Ballonnement", "Fatigue", "Nausée", "Stress"]
      : ["Headache", "Bloating", "Fatigue", "Nausea", "Stress"];
    defaults.forEach(d => {
      items.push({ type: 'symptom', label: d, data: d });
    });
    return items.slice(0, settings.maxSmartSuggestions);
  }, [settings.language, settings.maxSmartSuggestions]);

  // --- Analysis State ---
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult>({
    isReady: false,
    currentEntryCount: 0,
    requiredEntryCount: 10,
    daysTracked: 0,
    requiredDays: 3,
    // Initialize complex fields
    topSuspects: [],
    chemicalClusters: [],
    deepInsight: null,
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // ... (Other state - Connection, Modals, etc. unchanged)
  // --- Connection/Gemini State ---
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [mode, setMode] = useState<AppMode>('meal');
  const [volume, setVolume] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const activeSessionIdRef = useRef<string | null>(null);
  const liveClientRef = useRef<GeminiLiveClient | null>(null);

  // --- Popup & Modal States ---
  const [errorMessage, setErrorMessage] = useState<string | null>(null); 
  const hasLoggedRef = useRef(false);
  const [floatingAddMode, setFloatingAddMode] = useState<'meal' | 'symptom' | null>(null); 
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isBarcodeOpen, setIsBarcodeOpen] = useState(false); 
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isFavNameModalOpen, setIsFavNameModalOpen] = useState(false);
  const [pendingFavSessionId, setPendingFavSessionId] = useState<string | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [lastQuickAddSessionId, setLastQuickAddSessionId] = useState<string | null>(null);
  const [duplicateItemAlert, setDuplicateItemAlert] = useState<{show: boolean, name: string} | null>(null);
  const [rippleEffect, setRippleEffect] = useState<{id: number} | null>(null); 
  const [showWizard, setShowWizard] = useState(() => !localStorage.getItem('wizard_seen'));
  const [confirmModal, setConfirmModal] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void} | null>(null);
  const [pendingBarcodeProduct, setPendingBarcodeProduct] = useState<{
      name: string, 
      barcode: string, 
      ingredients: FoodItem[], 
      sessionId: string,
      categoriesTags?: string[]
  } | null>(null);

  // ... (Navigation, History, Persistance logic unchanged)
  const currentViewIndexRef = useRef(currentViewIndex);
  useEffect(() => { currentViewIndexRef.current = currentViewIndex; }, [currentViewIndex]);
  const modalsRef = useRef({ settings: showSettings, donation: showDonationModal, kitchen: showKitchen, water: showWaterModal, activity: showActivityModal, health: showHealthModal, scale: showScaleModal, pharmacy: showPharmacyModal, floatingAdd: floatingAddMode, manual: isManualEntryOpen, camera: isCameraOpen, barcode: isBarcodeOpen, pendingBarcode: pendingBarcodeProduct, favName: isFavNameModalOpen, wizard: showWizard });
  useEffect(() => { modalsRef.current = { settings: showSettings, donation: showDonationModal, kitchen: showKitchen, water: showWaterModal, activity: showActivityModal, health: showHealthModal, scale: showScaleModal, pharmacy: showPharmacyModal, floatingAdd: floatingAddMode, manual: isManualEntryOpen, camera: isCameraOpen, barcode: isBarcodeOpen, pendingBarcode: pendingBarcodeProduct, favName: isFavNameModalOpen, wizard: showWizard }; }, [showSettings, showDonationModal, showKitchen, showWaterModal, showActivityModal, showHealthModal, showScaleModal, showPharmacyModal, floatingAddMode, isManualEntryOpen, isCameraOpen, isBarcodeOpen, pendingBarcodeProduct, isFavNameModalOpen, showWizard]);

  const pushNavState = useCallback(() => { window.history.pushState({ appNav: true }, ""); }, []);
  const handleCloseWizard = useCallback(() => {
      setShowWizard(false);
      const idx = currentViewIndexRef.current;
      if (idx === 0) { localStorage.setItem('wizard_seen', 'true'); }
      else if (idx === 1) { localStorage.setItem('wizard_meal_seen', 'true'); }
      else if (idx === 2) { localStorage.setItem('wizard_symptom_seen', 'true'); }
  }, []);
  const handleCloseWizardRef = useRef(handleCloseWizard);
  useEffect(() => { handleCloseWizardRef.current = handleCloseWizard; }, [handleCloseWizard]);

  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
        const m = modalsRef.current;
        if (m.pendingBarcode) { setPendingBarcodeProduct(null); return; }
        if (m.barcode) { setIsBarcodeOpen(false); return; }
        if (m.camera) { setIsCameraOpen(false); return; }
        if (m.manual) { setIsManualEntryOpen(false); return; }
        if (m.floatingAdd) { setFloatingAddMode(null); return; }
        if (m.favName) { setIsFavNameModalOpen(false); return; }
        if (m.scale) { setShowScaleModal(false); return; }
        if (m.pharmacy) { setShowPharmacyModal(false); return; }
        if (m.kitchen) { setShowKitchen(false); return; }
        if (m.water) { setShowWaterModal(false); return; }
        if (m.activity) { setShowActivityModal(false); return; }
        if (m.health) { setShowHealthModal(false); return; }
        if (m.donation) { setShowDonationModal(false); return; }
        if (m.settings) { setShowSettings(false); return; }
        if (m.wizard) { handleCloseWizardRef.current(); return; }
        if (currentViewIndexRef.current !== 0) { setCurrentViewIndex(0); }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => { if (showWizard) { pushNavState(); } }, []); 

  const openSettings = () => { pushNavState(); setShowSettings(true); };
  const openKitchen = () => { pushNavState(); setShowKitchen(true); };
  const openWater = () => { pushNavState(); setShowWaterModal(true); };
  const openActivity = () => { pushNavState(); setShowActivityModal(true); };
  const openHealth = () => { pushNavState(); setShowHealthModal(true); };
  const openPharmacy = () => { pushNavState(); setShowPharmacyModal(true); };
  const openAddMeal = () => { pushNavState(); setFloatingAddMode('meal'); };
  const openAddSymptom = () => { pushNavState(); setFloatingAddMode('symptom'); };
  const openWizardTour = () => { pushNavState(); setShowWizard(true); };
  const openScaleMeasure = () => { pushNavState(); setShowScaleModal(true); };
  const openDonation = () => { pushNavState(); setShowDonationModal(true); };

  const handleViewChange = (index: number) => {
      if (index !== 0 && currentViewIndex === 0) pushNavState();
      setCurrentViewIndex(index);
  };
  const handleCalendarSelect = (date: Date) => {
      setSelectedDate(date);
      setIsCalendarExpanded(false);
  };

  useEffect(() => {
    const savedSessions = localStorage.getItem('vital_sessions');
    const savedSymptoms = localStorage.getItem('vital_symptoms');
    const savedFavorites = localStorage.getItem('vital_favorites');
    const savedRecipes = localStorage.getItem('vital_recipes');
    const savedPantry = localStorage.getItem('vital_pantry');
    const savedSettings = localStorage.getItem('vital_settings');
    const savedAnalysis = localStorage.getItem('vital_analysis');
    const savedWater = localStorage.getItem('vital_water');
    const savedActivity = localStorage.getItem('vital_activity'); 
    const savedScale = localStorage.getItem('vital_scale');
    const savedMeds = localStorage.getItem('vital_medications');
    const savedMedLogs = localStorage.getItem('vital_med_logs');

    if (savedSessions) {
        const parsed = JSON.parse(savedSessions).map((s: any) => ({
            ...s,
            timestamp: new Date(s.timestamp),
            items: s.items.map((i: any) => ({ ...i, timestamp: new Date(i.timestamp) }))
        }));
        setSessions(parsed);
    }
    if (savedSymptoms) {
        const parsed = JSON.parse(savedSymptoms).map((s: any) => ({
            ...s,
            timestamp: new Date(s.timestamp),
            items: s.items.map((i: any) => ({ ...i, timestamp: new Date(i.timestamp) }))
        }));
        setSymptomSessions(parsed);
    }
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
    if (savedRecipes) setCustomRecipes(JSON.parse(savedRecipes));
    if (savedPantry) setPantryItems(JSON.parse(savedPantry));
    if (savedSettings) setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
    if (savedAnalysis) setAnalysisResult(JSON.parse(savedAnalysis));
    if (savedWater) {
        const parsedWater = JSON.parse(savedWater).map((w: any) => ({ ...w, timestamp: new Date(w.timestamp) }));
        setWaterLogs(parsedWater);
    }
    if (savedActivity) {
        const parsedActivity = JSON.parse(savedActivity).map((a: any) => ({ ...a, timestamp: new Date(a.timestamp) }));
        setActivityLogs(parsedActivity);
    }
    if (savedScale) {
        const parsedScale = JSON.parse(savedScale).map((s: any) => ({ ...s, timestamp: new Date(s.timestamp) }));
        setScaleLogs(parsedScale);
    }
    if (savedMeds) {
        const parsedMeds = JSON.parse(savedMeds).map((m: any) => ({
            ...m,
            expirationDate: m.expirationDate ? new Date(m.expirationDate) : undefined,
            addedAt: m.addedAt ? new Date(m.addedAt) : new Date()
        }));
        setMedications(parsedMeds);
    }
    if (savedMedLogs) {
        setMedicationLogs(JSON.parse(savedMedLogs).map((l: any) => ({ ...l, timestamp: new Date(l.timestamp) })));
    }
  }, []);

  useEffect(() => { localStorage.setItem('vital_sessions', JSON.stringify(sessions)); sessionsRef.current = sessions; }, [sessions]);
  useEffect(() => { localStorage.setItem('vital_symptoms', JSON.stringify(symptomSessions)); symptomSessionsRef.current = symptomSessions; }, [symptomSessions]);
  useEffect(() => { localStorage.setItem('vital_favorites', JSON.stringify(favorites)); }, [favorites]);
  useEffect(() => { localStorage.setItem('vital_recipes', JSON.stringify(customRecipes)); customRecipesRef.current = customRecipes; }, [customRecipes]);
  useEffect(() => { localStorage.setItem('vital_pantry', JSON.stringify(pantryItems)); pantryItemsRef.current = pantryItems; }, [pantryItems]);
  useEffect(() => { localStorage.setItem('vital_settings', JSON.stringify(settings)); }, [settings]);
  useEffect(() => { localStorage.setItem('vital_analysis', JSON.stringify(analysisResult)); }, [analysisResult]);
  useEffect(() => { localStorage.setItem('vital_water', JSON.stringify(waterLogs)); }, [waterLogs]);
  useEffect(() => { localStorage.setItem('vital_activity', JSON.stringify(activityLogs)); }, [activityLogs]); 
  useEffect(() => { localStorage.setItem('vital_scale', JSON.stringify(scaleLogs)); }, [scaleLogs]);
  useEffect(() => { localStorage.setItem('vital_medications', JSON.stringify(medications)); }, [medications]);
  useEffect(() => { localStorage.setItem('vital_med_logs', JSON.stringify(medicationLogs)); }, [medicationLogs]);

  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  }, [settings.theme]);

  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);

  // Update analysis readiness status
  useEffect(() => {
    const totalEntries = sessions.length;
    const uniqueDays = new Set(sessions.map(s => new Date(s.timestamp).toDateString()));
    const daysTracked = uniqueDays.size;
    setAnalysisResult(prev => ({
        ...prev,
        currentEntryCount: totalEntries,
        daysTracked: daysTracked,
        isReady: totalEntries >= 5 && daysTracked >= 1 // Lowered threshold for v0.2
    }));
  }, [sessions]);

  // ... (Helper functions: getDailySessions, getNewSessionDate, handleToggleStar, etc. - UNCHANGED)
  const getDailySessions = <T extends { id: string; timestamp: Date }>(list: T[], date: Date) => {
      return list.filter(item => new Date(item.timestamp).toDateString() === date.toDateString())
                 .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  };
  const getNewSessionDate = useCallback(() => {
      const now = new Date();
      const newDate = new Date(selectedDate);
      newDate.setHours(now.getHours());
      newDate.setMinutes(now.getMinutes());
      newDate.setSeconds(now.getSeconds());
      return newDate;
  }, [selectedDate]);
  const handleToggleStar = (sessionId: string) => {
      const session = sessions.find(s => s.id === sessionId);
      if (!session) return;
      if (session.isStarred) {
          setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, isStarred: false } : s));
          setFavorites(prev => prev.filter(f => f.id !== session.favoriteId));
      } else {
          setPendingFavSessionId(sessionId);
          pushNavState();
          setIsFavNameModalOpen(true);
      }
  };
  const handleSaveFavorite = (name: string, time: string | undefined) => {
      if (!pendingFavSessionId) return;
      const session = sessions.find(s => s.id === pendingFavSessionId);
      if (!session) return;
      const favId = crypto.randomUUID();
      const newFavorite: FavoriteMeal = { id: favId, name: name, items: session.items, usualTime: time || undefined, mealType: session.manualMealType };
      setFavorites(prev => [...prev, newFavorite]);
      setSessions(prev => prev.map(s => s.id === pendingFavSessionId ? { ...s, isStarred: true, favoriteId: favId } : s));
      setPendingFavSessionId(null);
  };
  const handleDeleteFavorite = (id: string) => {
      setFavorites(prev => prev.filter(f => f.id !== id));
      setSessions(prev => prev.map(s => s.favoriteId === id ? { ...s, isStarred: false, favoriteId: undefined } : s));
  };
  const handleUpdateFavorite = (favorite: FavoriteMeal) => {
      setFavorites(prev => prev.map(f => f.id === favorite.id ? favorite : f));
  };
  const handleSaveRecipe = (recipe: CustomRecipe) => {
      setCustomRecipes(prev => {
          const filtered = prev.filter(r => r.name.toLowerCase() !== recipe.name.toLowerCase());
          return [...filtered, recipe];
      });
  };
  const handleDeleteRecipe = (id: string) => setCustomRecipes(prev => prev.filter(r => r.id !== id));
  const handleSavePantryItem = (item: PantryItem) => {
      setPantryItems(prev => {
          const filtered = prev.filter(p => p.id !== item.id);
          return [...filtered, item];
      });
  };
  const handleDeletePantryItem = (id: string) => setPantryItems(prev => prev.filter(p => p.id !== id));
  const getTargetSessionId = useCallback((type: 'meal' | 'symptom' = 'meal') => {
      const MERGE_THRESHOLD_MS = 120000; 
      const now = getNewSessionDate();
      const targetTime = now.getTime();
      const candidateList = type === 'meal' ? sessionsRef.current : symptomSessionsRef.current;
      const sameDaySessions = candidateList.filter(s => new Date(s.timestamp).toDateString() === now.toDateString());
      const candidates = sameDaySessions.map(s => ({ id: s.id, diff: Math.abs(targetTime - new Date(s.timestamp).getTime()) })).filter(c => c.diff < MERGE_THRESHOLD_MS).sort((a, b) => a.diff - b.diff);
      if (candidates.length > 0) return candidates[0].id;
      const newId = crypto.randomUUID();
      if (type === 'meal') { setSessions(prev => [{ id: newId, timestamp: getNewSessionDate(), items: [] }, ...prev]); setLastQuickAddSessionId(newId); }
      else { setSymptomSessions(prev => [{ id: newId, timestamp: getNewSessionDate(), items: [] }, ...prev]); }
      return newId;
  }, [getNewSessionDate]);
  const handleMergeSessions = (sourceId: string, targetId: string, type: 'meal' | 'symptom') => {
      if (sourceId === targetId) return;
      if (type === 'meal') {
          const source = sessions.find(s => s.id === sourceId);
          const target = sessions.find(s => s.id === targetId);
          if (!source || !target) return;
          setSessions(prev => {
              const filtered = prev.filter(s => s.id !== sourceId);
              return filtered.map(s => {
                  if (s.id === targetId) {
                      const newItems = [...source.items, ...s.items];
                      const uniqueItems: FoodItem[] = [];
                      const seen = new Set<string>();
                      newItems.forEach(i => {
                          const key = `${i.name.toLowerCase()}-${i.quantity}-${i.unit}`;
                          if (!seen.has(key)) { seen.add(key); uniqueItems.push(i); }
                      });
                      return { ...s, items: uniqueItems };
                  }
                  return s;
              });
          });
      } else {
          const source = symptomSessions.find(s => s.id === sourceId);
          const target = symptomSessions.find(s => s.id === targetId);
          if (!source || !target) return;
          setSymptomSessions(prev => {
              const filtered = prev.filter(s => s.id !== sourceId);
              return filtered.map(s => {
                  if (s.id === targetId) {
                      return { ...s, items: [...source.items, ...s.items] };
                  }
                  return s;
              });
          });
      }
      setExpandedSessionId(targetId);
  };
  const showDuplicateAlert = (name: string) => {
      setDuplicateItemAlert({ show: true, name });
      setTimeout(() => setDuplicateItemAlert(null), 3000);
  };
  const showErrorMessage = (msg: string) => {
      setErrorMessage(msg);
      setTimeout(() => setErrorMessage(null), 4000);
  };

  const processAndAddFoodItems = useCallback(async (sessionId: string, items: {name: string, quantity: number, unit: string}[], forcePantryItem?: PantryItem) => {
      const currentRecipes = customRecipesRef.current;
      const currentPantry = pantryItemsRef.current; 
      const newItems: FoodItem[] = [];
      const defaultUnit = t(settings.language, 'u_piece');
      
      for (const i of items) {
          let finalName = i.name;
          const inputLower = i.name.trim().toLowerCase();
          
          const customRecMatch = currentRecipes.find(r => r.name.toLowerCase() === inputLower);
          const pantryMatch = forcePantryItem && forcePantryItem.name.toLowerCase() === inputLower ? forcePantryItem : currentPantry.find(p => p.name.toLowerCase() === inputLower);
          const dbMatch = normalizeFood(i.name, settings.language);
          
          if (!customRecMatch && !pantryMatch && !dbMatch) {
              finalName = i.name.charAt(0).toUpperCase() + i.name.slice(1);
          } else {
              if (customRecMatch) finalName = customRecMatch.name; 
              else if (pantryMatch) finalName = pantryMatch.name;
              else if (dbMatch) finalName = dbMatch; 
          }

          let ingredients: FoodItem[] = [];
          let finalQuantity = i.quantity;
          let finalUnit = i.unit || defaultUnit;
          
          const customRec = currentRecipes.find(r => r.name.toLowerCase() === finalName.toLowerCase());
          const pantryItem = pantryMatch && pantryMatch.name.toLowerCase() === finalName.toLowerCase() ? pantryMatch : null;
          
          if (customRec) { 
              ingredients = customRec.ingredients.map(ing => ({ ...ing, id: crypto.randomUUID(), timestamp: new Date() })); 
          } else if (pantryItem) { 
              ingredients = pantryItem.ingredients.map(ing => ({ ...ing, id: crypto.randomUUID(), timestamp: new Date() })); 
              if (i.quantity === 1 && pantryItem.quantity !== undefined) { 
                  finalQuantity = pantryItem.quantity; 
                  finalUnit = pantryItem.unit || finalUnit; 
              } 
          } else {
              const dbFood = dbFoodsList.find(f => f[settings.language].toLowerCase() === finalName.toLowerCase());
              if (dbFood) {
                  const baseIngKeys = getDecomposition(dbFood.id);
                  if (baseIngKeys) {
                      ingredients = baseIngKeys.map(key => {
                          const translatedName = getFoodNameById(key, settings.language) || key;
                          return {
                              id: crypto.randomUUID(),
                              name: translatedName,
                              quantity: 1, 
                              unit: defaultUnit,
                              timestamp: new Date()
                          };
                      });
                  }
              }
          }

          newItems.push({ 
              id: crypto.randomUUID(), 
              name: finalName, 
              quantity: finalQuantity, 
              unit: finalUnit, 
              timestamp: getNewSessionDate(), 
              ingredients: ingredients, 
              isComposite: ingredients.length > 0, 
              isPantry: !!pantryItem 
          });
      }

      if (newItems.length > 0) {
          setSessions(prev => prev.map(s => {
              if (s.id === sessionId) {
                  const filteredNewItems = newItems.filter(newItem => {
                      const newNameLower = newItem.name.toLowerCase();
                      return !s.items.some(existing => {
                          const existingLower = existing.name.toLowerCase();
                          if (existingLower === newNameLower) return true; 
                          if (existingLower.includes(newNameLower) && (existingLower.length - newNameLower.length) < 10) return true;
                          return false;
                      });
                  });
                  
                  return { ...s, items: [...filteredNewItems, ...s.items] };
              }
              return s;
          }));
          handleViewChange(1);
      }
  }, [settings.language, getNewSessionDate]);

  const handleLogFavorite = (favorite: FavoriteMeal) => {
      const targetId = getTargetSessionId('meal');
      setExpandedSessionId(targetId);
      if (currentViewIndexRef.current !== 1) handleViewChange(1); 
      
      setSessions(prev => {
          return prev.map(s => {
              if (s.id === targetId) {
                  const itemsToAdd = favorite.items.filter(favItem => 
                      !s.items.some(existing => existing.name.toLowerCase() === favItem.name.toLowerCase())
                  );

                  if (itemsToAdd.length === 0) return s;

                  const newItems = itemsToAdd.map(i => ({ 
                      ...i, 
                      id: crypto.randomUUID(), 
                      timestamp: getNewSessionDate() 
                  }));

                  const shouldInheritType = s.items.length === 0 && !s.manualMealType;
                  
                  return { 
                      ...s, 
                      items: [...newItems, ...s.items], 
                      manualMealType: shouldInheritType ? favorite.mealType : s.manualMealType 
                  };
              }
              return s;
          });
      });
  };

  const handleLogSuggestion = (suggestion: SmartSuggestionItem) => {
      if (suggestion.type === 'favorite') handleLogFavorite(suggestion.data);
      else if (suggestion.type === 'item') handleLogFoodItem(suggestion.data);
      else if (suggestion.type === 'symptom') handleLogSymptomItem(suggestion.data);
  };
  const handleLogFoodItem = (itemName: string) => {
      const targetId = getTargetSessionId('meal');
      setExpandedSessionId(targetId);
      const targetSession = sessionsRef.current.find(s => s.id === targetId);
      if (targetSession && targetSession.items.some(i => i.name.toLowerCase() === itemName.toLowerCase())) {
          showDuplicateAlert(itemName); 
          handleViewChange(1); 
          return;
      }
      processAndAddFoodItems(targetId, [{ name: itemName, quantity: 1, unit: '' }]);
  };
  const handleLogSymptomItem = (symptomName: string) => {
      const normalized = normalizeSymptom(symptomName, settings.language);
      if (!normalized) { showErrorMessage(`"${symptomName}" ${t(settings.language, 'notInDatabase')}`); return; }
      const targetId = getTargetSessionId('symptom');
      setExpandedSessionId(targetId);
      const newItem: SymptomItem = { id: crypto.randomUUID(), symptom: normalized, severity: 5, timestamp: getNewSessionDate() };
      setSymptomSessions(prev => prev.map(s => s.id === targetId ? { ...s, items: [newItem, ...s.items] } : s));
      handleViewChange(2);
  };
  const handleAddWater = (amount: number) => setWaterLogs(prev => [{ id: crypto.randomUUID(), amount, timestamp: new Date() }, ...prev]);
  const handleDeleteWaterLog = (id: string) => setWaterLogs(prev => prev.filter(l => l.id !== id));
  const handleQuickAddWater = () => { handleAddWater(settings.standardWaterVolume); setRippleEffect({ id: Date.now() }); setTimeout(() => setRippleEffect(null), 1000); };
  
  const handleSaveActivity = (log: ActivityLog) => setActivityLogs(prev => [log, ...prev]);
  const handleUpdateActivity = (id: string, updatedLog: ActivityLog) => setActivityLogs(prev => prev.map(l => l.id === id ? updatedLog : l));
  const handleDeleteActivity = (id: string) => setActivityLogs(prev => prev.filter(l => l.id !== id));

  const handleSaveScaleLog = (log: ScaleLog) => {
      setScaleLogs(prev => [log, ...prev]);
      if (log.weight) { setSettings(prev => ({ ...prev, weight: log.weight })); }
  };
  const handleDeleteScaleLog = (id: string) => setScaleLogs(prev => prev.filter(l => l.id !== id));

  const handleAddMedication = (med: Medication) => { setMedications(prev => [...prev, med]); };
  const handleDeleteMedication = (id: string) => { setMedications(prev => prev.filter(m => m.id !== id)); };
  const handleTakeMedication = (medId: string, amount: number) => {
      const med = medications.find(m => m.id === medId);
      if (med && med.stock >= amount) {
          setMedications(prev => prev.map(m => m.id === medId ? { ...m, stock: m.stock - amount } : m));
          const newLog: MedicationLog = { id: crypto.randomUUID(), medicationId: medId, medicationName: med.name, timestamp: new Date(), amountTaken: amount };
          setMedicationLogs(prev => [newLog, ...prev]);
      }
  };

  const waterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleWaterClick = () => {
      if (waterTimerRef.current) { clearTimeout(waterTimerRef.current); waterTimerRef.current = null; handleQuickAddWater(); }
      else { waterTimerRef.current = setTimeout(() => { openWater(); waterTimerRef.current = null; }, 250); }
  };

  const handleFoodLogged = useCallback((args: LogFoodArgs) => {
    const currentSessionId = activeSessionIdRef.current;
    if (!currentSessionId) return;
    hasLoggedRef.current = true;
    processAndAddFoodItems(currentSessionId, [{ name: args.name, quantity: args.quantity || 1, unit: args.unit || '' }]);
  }, [processAndAddFoodItems]);

  const handleSymptomLogged = useCallback((args: LogSymptomArgs) => {
    const currentSessionId = activeSessionIdRef.current;
    if (!currentSessionId) return;
    const normalizedName = normalizeSymptom(args.symptom, settings.language);
    if (!normalizedName) { showErrorMessage(`"${args.symptom}" ${t(settings.language, 'notInDatabase')}`); return; }
    
    hasLoggedRef.current = true;
    let numSeverity = 5;
    if (args.severity === 'mild') numSeverity = 2;
    if (args.severity === 'moderate') numSeverity = 5;
    if (args.severity === 'severe') numSeverity = 8;
    
    setSymptomSessions(prev => prev.map(session => {
        if (session.id === currentSessionId) {
            const existingItemIndex = session.items.findIndex(item => item.symptom.toLowerCase() === normalizedName.toLowerCase());
            if (existingItemIndex > -1) {
                const newItems = [...session.items];
                newItems[existingItemIndex] = { ...newItems[existingItemIndex], severity: numSeverity, timestamp: getNewSessionDate() };
                return { ...session, items: newItems };
            } else {
                const newItem: SymptomItem = { id: crypto.randomUUID(), symptom: normalizedName, severity: numSeverity, timestamp: getNewSessionDate() };
                return { ...session, items: [newItem, ...session.items] };
            }
        }
        return session;
    }));
    handleViewChange(2);
  }, [getNewSessionDate, settings.language]);

  const handleUpdateSessionType = useCallback((sessionId: string, type: MealTypeKey) => { setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, manualMealType: type } : s)); }, []);

  const startNewSession = async (targetMode: 'meal' | 'symptom', initialMealType?: MealTypeKey) => { 
      if (status === 'connected' || status === 'connecting') {
          if (mode === 'command') liveClientRef.current?.disconnect();
          else return;
      }
      hasLoggedRef.current = false; 
      const targetId = getTargetSessionId(targetMode); 
      activeSessionIdRef.current = targetId; 
      setActiveSessionId(targetId); 
      setExpandedSessionId(targetId); 
      handleViewChange(targetMode === 'meal' ? 1 : 2); 
      if (targetMode === 'meal' && initialMealType) handleUpdateSessionType(targetId, initialMealType);
      await connectClient(targetMode); 
  };
  const handleCommandReceived = useCallback((args: VoiceCommandArgs) => {
      if (args.command === 'start_meal') startNewSession('meal', args.mealType);
      else if (args.command === 'start_symptom') startNewSession('symptom');
  }, [startNewSession]);
  const handleManualAddFoods = useCallback(async (sessionId: string, items: ParsedItem[]) => {
    await processAndAddFoodItems(sessionId, items.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit })));
  }, [processAndAddFoodItems]);
  const handleManualAddSymptoms = useCallback(async (sessionId: string, items: ParsedItem[]) => {
    const validItems: SymptomItem[] = [];
    const invalidNames: string[] = [];
    const now = getNewSessionDate();
    items.forEach(item => {
        const normalizedName = normalizeSymptom(item.name, settings.language);
        if (normalizedName) validItems.push({ id: crypto.randomUUID(), symptom: normalizedName, severity: item.severity || 5, timestamp: now });
        else invalidNames.push(item.name);
    });
    if (invalidNames.length > 0) showErrorMessage(`${t(settings.language, 'rejectedSymptoms')}: ${invalidNames.join(', ')}`);
    if (validItems.length > 0) {
        setSymptomSessions(prev => prev.map(session => { 
            if (session.id === sessionId) {
                const existingNames = new Set(session.items.map(i => i.symptom.toLowerCase()));
                const uniqueNewItems = validItems.filter(i => !existingNames.has(i.symptom.toLowerCase()));
                return { ...session, items: [...uniqueNewItems, ...session.items] }; 
            }
            return session; 
        }));
    }
    handleViewChange(2);
  }, [getNewSessionDate, settings.language]);

  const handleCreateNewSessionManually = async (items: ParsedItem[]) => {
      const targetMode = floatingAddMode === 'meal' ? 'meal' : 'symptom';
      const targetId = getTargetSessionId(targetMode);
      if (targetMode === 'meal') { 
          setExpandedSessionId(targetId); 
          await handleManualAddFoods(targetId, items); 
      } else {
          await handleManualAddSymptoms(targetId, items);
      }
      setFloatingAddMode(null);
  };

  const handleFloatingMethodSelect = (method: 'audio' | 'text' | 'barcode' | 'camera') => {
      if (method === 'audio') {
          if (floatingAddMode) {
              startNewSession(floatingAddMode);
              setFloatingAddMode(null);
          }
      } else if (method === 'text') {
          setIsManualEntryOpen(true);
      } else if (method === 'barcode') {
          setIsBarcodeOpen(true);
      } else if (method === 'camera') {
          setIsCameraOpen(true);
      }
  };

  // --- UPDATED ANALYSIS FUNCTION ---
  const runAnalysis = async () => { 
      setIsAnalyzing(true); 
      
      // Ensure visual feedback duration
      const minDelay = new Promise(resolve => setTimeout(resolve, 1500));

      try { 
          const [result] = await Promise.all([
              runFullAnalysis(sessions, symptomSessions, settings.language),
              minDelay
          ]);
          // Force a new object reference and update timestamp to ensure re-render
          setAnalysisResult({ ...result, lastAnalyzed: new Date() });
      }
      catch (e) { 
          console.error("Analysis Error", e);
          alert(t(settings.language, 'importError') || "Analysis failed. Please try again.");
      } 
      finally { 
          setIsAnalyzing(false); 
      } 
  };
  // -------------------------------

  const handleDeleteFoodItem = useCallback((sessionId: string, itemId: string) => { setSessions(prev => prev.map(session => session.id === sessionId ? { ...session, items: session.items.filter(item => item.id !== itemId) } : session)); }, []);
  const handleUpdateFoodItem = useCallback((sessionId: string, itemId: string, quantity: number, unit: string) => { 
      setSessions(prev => prev.map(session => { 
          if (session.id === sessionId) {
              return { 
                  ...session, 
                  items: session.items.map(item => {
                      if (item.id === itemId) {
                          const oldQty = item.quantity || 1;
                          let newIngredients = item.ingredients;
                          if (item.ingredients && item.ingredients.length > 0 && oldQty > 0 && quantity > 0) {
                              const ratio = quantity / oldQty;
                              newIngredients = item.ingredients.map(ing => ({ ...ing, quantity: Number((ing.quantity * ratio).toFixed(2)) }));
                          }
                          return { ...item, quantity, unit, ingredients: newIngredients };
                      } 
                      return item;
                  }) 
              }; 
          }
          return session; 
      })); 
  }, []);
  const handleUpdateRecipe = useCallback((sessionId: string, itemId: string, updatedItem: FoodItem, saveAsCustom: boolean) => {
      setSessions(prev => prev.map(session => session.id === sessionId ? { ...session, items: session.items.map(item => item.id === itemId ? updatedItem : item) } : session));
      if (saveAsCustom) {
          const newRecipe: CustomRecipe = { id: crypto.randomUUID(), name: updatedItem.name, ingredients: updatedItem.ingredients || [] };
          setCustomRecipes(prev => [...prev.filter(r => r.name.toLowerCase() !== newRecipe.name.toLowerCase()), newRecipe]);
      }
  }, []);
  const handleDeleteSymptomItem = useCallback((sessionId: string, itemId: string) => { 
      setConfirmModal({
          isOpen: true, title: t(settings.language, 'deleteSymptom'), message: t(settings.language, 'deleteItemConfirm'),
          onConfirm: () => {
              setSymptomSessions(prev => prev.map(session => session.id === sessionId ? { ...session, items: session.items.filter(item => item.id !== itemId) } : session));
              setConfirmModal(null);
          }
      });
  }, [settings.language]);
  const handleUpdateSymptomItem = useCallback((sessionId: string, itemId: string, newSeverity: number) => { setSymptomSessions(prev => prev.map(session => session.id === sessionId ? { ...session, items: session.items.map(item => item.id === itemId ? { ...item, severity: newSeverity } : item) } : session)); }, []);
  const handleDeleteMealSession = useCallback((sessionId: string) => { 
      setConfirmModal({
          isOpen: true, title: t(settings.language, 'deleteSessionTooltip'), message: t(settings.language, 'deleteSessionConfirm'),
          onConfirm: () => {
              setSessions(prev => prev.filter(s => s.id !== sessionId));
              setConfirmModal(null);
          }
      });
  }, [settings.language]);
  const handleDeleteSymptomSession = useCallback((sessionId: string) => { 
      setConfirmModal({
          isOpen: true, title: t(settings.language, 'deleteSessionTooltip'), message: t(settings.language, 'deleteSessionConfirm'),
          onConfirm: () => {
              setSymptomSessions(prev => prev.filter(s => s.id !== sessionId));
              setConfirmModal(null);
          }
      });
  }, [settings.language]);
  const handleUpdateSessionTime = useCallback((sessionId: string, newDate: Date, type: 'meal' | 'symptom') => { if (type === 'meal') setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, timestamp: newDate } : s).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())); else setSymptomSessions(prev => prev.map(s => s.id === sessionId ? { ...s, timestamp: newDate } : s).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())); }, []);
  const connectClient = async (targetMode: AppMode) => {
      if (liveClientRef.current) liveClientRef.current.disconnect();
      setLiveTranscript('');
      setMode(targetMode);
      const client = new GeminiLiveClient({
        onStatusChange: (newStatus) => {
            setStatus(newStatus);
            if (newStatus === 'disconnected') {
                setLiveTranscript('');
                if (hasLoggedRef.current) { hasLoggedRef.current = false; }
            }
        },
        onFoodLogged: handleFoodLogged,
        onSymptomLogged: handleSymptomLogged,
        onCommandReceived: handleCommandReceived,
        onVolumeChange: (inputVol) => setVolume(inputVol),
        onTranscriptionUpdate: (text) => setLiveTranscript(text),
        onError: (err) => { console.error(err); setStatus('error'); setActiveSessionId(null); activeSessionIdRef.current = null; setLiveTranscript(''); }
      });
      liveClientRef.current = client;
      const pantryNames = pantryItemsRef.current.map(p => p.name).join(', ');
      const extraPrompt = pantryNames ? ` Note : L'utilisateur a ces produits favoris : ${pantryNames}.` : '';
      await client.connect(targetMode, settings.language, extraPrompt);
  };
  const resumeSession = async (sessionId: string, targetMode: AppMode) => { 
      if (status === 'connected' || status === 'connecting') { liveClientRef.current?.disconnect(); return; } 
      hasLoggedRef.current = false; activeSessionIdRef.current = sessionId; setActiveSessionId(sessionId); 
      handleViewChange(targetMode === 'meal' ? 1 : 2);
      await connectClient(targetMode); 
  };
  const handleFabClick = () => { if (status === 'connected') liveClientRef.current?.disconnect(); };
  useEffect(() => { return () => { liveClientRef.current?.disconnect(); }; }, []);
  const handleImageCapture = async (imageSrc: string) => {
      setIsCameraOpen(false); setIsProcessingImage(true); 
      let sessionId = activeSessionId;
      if (!sessionId || status === 'disconnected') { sessionId = getTargetSessionId('meal'); setExpandedSessionId(sessionId); }
      const detectedFoods = await analyzeFoodImage(imageSrc, settings.language);
      const parsedItems: ParsedItem[] = detectedFoods.map(f => ({ name: f.name, quantity: f.quantity, unit: f.unit }));
      if (floatingAddMode === 'meal' || activeSessionId) await handleManualAddFoods(sessionId, parsedItems);
      setIsProcessingImage(false); setFloatingAddMode(null);
  };
  const handleBarcodeDetected = async (barcode: string) => {
      setIsBarcodeOpen(false); setIsProcessingImage(true); 
      let targetSessionId = activeSessionId || getTargetSessionId('meal');
      setExpandedSessionId(targetSessionId);
      const product = await scanProduct(barcode);
      setIsProcessingImage(false);
      if (product) {
          const existingPantryItem = pantryItemsRef.current.find(p => p.barcode === barcode);
          if (existingPantryItem) {
              await processAndAddFoodItems(targetSessionId, [{ name: existingPantryItem.name, quantity: existingPantryItem.quantity || 1, unit: existingPantryItem.unit || '' }]);
              setFloatingAddMode(null); handleViewChange(1); 
          } else {
              pushNavState();
              setPendingBarcodeProduct({ name: product.name, barcode: barcode, ingredients: product.ingredients || [], sessionId: targetSessionId, categoriesTags: product.categoriesTags || [] });
          }
      } else { alert(t(settings.language, 'noData')); setFloatingAddMode(null); }
  };
  const savePendingBarcodeProduct = async (customName: string, location: StorageLocation, category: PantryCategory) => {
      if (!pendingBarcodeProduct) return;
      const newItem: PantryItem = { id: crypto.randomUUID(), name: customName, originalName: pendingBarcodeProduct.name, barcode: pendingBarcodeProduct.barcode, ingredients: pendingBarcodeProduct.ingredients, quantity: 1, unit: '', location: location, storageCategory: category };
      handleSavePantryItem(newItem);
      await processAndAddFoodItems(pendingBarcodeProduct.sessionId, [{ name: customName, quantity: 1, unit: '' }], newItem);
      setPendingBarcodeProduct(null); setFloatingAddMode(null); handleViewChange(1); 
  };
  const handleExportData = useCallback(() => {
    const exportData = { meals: sessions, symptoms: symptomSessions, customRecipes: customRecipes, pantryItems: pantryItems, favorites: favorites, waterLogs: waterLogs, activityLogs: activityLogs, scaleLogs: scaleLogs, medications: medications, medicationLogs: medicationLogs, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `vitaleat-export-${new Date().toISOString().split('T')[0]}.json`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }, [sessions, symptomSessions, customRecipes, pantryItems, favorites, waterLogs, activityLogs, scaleLogs, medications, medicationLogs]);

  // NEW: Import Data Handler
  const handleImportData = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        try {
            const json = JSON.parse(e.target?.result as string);
            
            // Basic validation
            if (!json.meals && !json.symptoms) throw new Error("Invalid file format");

            // Restore with Date parsing
            if (json.meals) setSessions(json.meals.map((s: any) => ({ ...s, timestamp: new Date(s.timestamp), items: s.items.map((i: any) => ({ ...i, timestamp: new Date(i.timestamp) })) })));
            if (json.symptoms) setSymptomSessions(json.symptoms.map((s: any) => ({ ...s, timestamp: new Date(s.timestamp), items: s.items.map((i: any) => ({ ...i, timestamp: new Date(i.timestamp) })) })));
            if (json.favorites) setFavorites(json.favorites);
            if (json.customRecipes) setCustomRecipes(json.customRecipes);
            if (json.pantryItems) setPantryItems(json.pantryItems);
            if (json.waterLogs) setWaterLogs(json.waterLogs.map((l: any) => ({ ...l, timestamp: new Date(l.timestamp) })));
            if (json.activityLogs) setActivityLogs(json.activityLogs.map((l: any) => ({ ...l, timestamp: new Date(l.timestamp) })));
            if (json.scaleLogs) setScaleLogs(json.scaleLogs.map((l: any) => ({ ...l, timestamp: new Date(l.timestamp) })));
            if (json.medications) setMedications(json.medications.map((m: any) => ({ ...m, expirationDate: m.expirationDate ? new Date(m.expirationDate) : undefined, addedAt: new Date(m.addedAt || new Date()) })));
            if (json.medicationLogs) setMedicationLogs(json.medicationLogs.map((l: any) => ({ ...l, timestamp: new Date(l.timestamp) })));
            
            // Force save to local storage immediately to ensure persistence
            alert(t(settings.language, 'importSuccess'));
        } catch (err) {
            console.error("Import Failed", err);
            alert(t(settings.language, 'importError'));
        }
    };
    reader.readAsText(file);
  }, [settings.language]);

  if (!isLoggedIn) return <LoginView initialLang={settings.language} onLogin={(lang) => { setSettings(s => ({ ...s, language: lang })); setIsLoggedIn(true); }} />;

  return (
    <div className="h-[100dvh] bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden transition-colors relative">
      <Header 
        onOpenKitchen={openKitchen} 
        onOpenHealth={openHealth} 
        onOpenPharmacy={openPharmacy}
        onOpenSettings={openSettings} 
        onOpenWizard={openWizardTour} 
        isRecording={status === 'connected'} 
      />
      {showSettings && <SettingsView settings={settings} onUpdateSettings={setSettings} onLogout={() => setIsLoggedIn(false)} onClose={() => { window.history.back(); }} onExportData={handleExportData} onImportData={handleImportData} onOpenDonation={openDonation} />}
      {showKitchen && <KitchenModal isOpen={showKitchen} onClose={() => { window.history.back(); }} recipes={customRecipes} pantryItems={pantryItems} favorites={favorites} onSaveRecipe={handleSaveRecipe} onDeleteRecipe={handleDeleteRecipe} onSavePantryItem={handleSavePantryItem} onDeletePantryItem={handleDeletePantryItem} onDeleteFavorite={handleDeleteFavorite} onUpdateFavorite={handleUpdateFavorite} language={settings.language} />}
      {showWaterModal && <WaterModal isOpen={showWaterModal} onClose={() => { window.history.back(); }} logs={waterLogs} activityLogs={activityLogs} scaleLogs={scaleLogs} onAddWater={handleAddWater} onDeleteLog={handleDeleteWaterLog} settings={settings} onUpdateSettings={setSettings} />}
      {showHealthModal && <HealthModal isOpen={showHealthModal} onClose={() => { window.history.back(); }} onOpenScaleMeasure={openScaleMeasure} onOpenActivity={openActivity} language={settings.language} />}
      {showScaleModal && <ScaleModal isOpen={showScaleModal} onClose={() => { window.history.back(); }} logs={scaleLogs} onSave={handleSaveScaleLog} onDelete={handleDeleteScaleLog} language={settings.language} currentWeight={settings.weight} />}
      {showActivityModal && <ActivityModal isOpen={showActivityModal} onClose={() => { window.history.back(); }} logs={activityLogs} onSave={handleSaveActivity} onUpdate={handleUpdateActivity} onDelete={handleDeleteActivity} language={settings.language} />}
      {showPharmacyModal && <PharmacyModal isOpen={showPharmacyModal} onClose={() => { window.history.back(); }} medications={medications} medicationLogs={medicationLogs} onAddMedication={handleAddMedication} onDeleteMedication={handleDeleteMedication} onTakeMedication={handleTakeMedication} language={settings.language} />}
      {showDonationModal && <DonationModal isOpen={showDonationModal} onClose={() => { window.history.back(); }} language={settings.language} />}
      <Wizard isOpen={showWizard} onClose={() => { handleCloseWizard(); if(showWizard) window.history.back(); }} language={settings.language} currentViewIndex={currentViewIndex} />
      {floatingAddMode && !isManualEntryOpen && !isCameraOpen && !isBarcodeOpen && !pendingBarcodeProduct && (
          <AddMethodModal isOpen={true} onClose={() => { window.history.back(); }} onSelect={handleFloatingMethodSelect} language={settings.language} type={floatingAddMode} />
      )}
      {isManualEntryOpen && (
          <ManualEntryModal isOpen={true} onClose={() => { window.history.back(); }} type={floatingAddMode === 'meal' ? 'food' : 'symptom'} onSave={(items) => handleCreateNewSessionManually(items)} language={settings.language} usedItemsHistory={floatingAddMode === 'meal' ? foodHistory : symptomHistory} customRecipes={customRecipes} pantryItems={pantryItems} />
      )}
      {isCameraOpen && <CameraCaptureModal isOpen={isCameraOpen} onClose={() => { window.history.back(); }} onCapture={handleImageCapture} language={settings.language} />}
      {isBarcodeOpen && <BarcodeScannerModal isOpen={isBarcodeOpen} onClose={() => { window.history.back(); }} onDetected={handleBarcodeDetected} language={settings.language} />}
      
      {pendingBarcodeProduct && (
          <SaveProductModal 
              isOpen={true} 
              onClose={() => { window.history.back(); }} 
              product={pendingBarcodeProduct} 
              onSave={savePendingBarcodeProduct} 
              language={settings.language} 
          />
      )}

      <ConfirmationModal isOpen={!!confirmModal} onClose={() => setConfirmModal(null)} onConfirm={confirmModal?.onConfirm || (() => {})} title={confirmModal?.title || ''} message={confirmModal?.message || ''} language={settings.language} />
      {isFavNameModalOpen && <FavoriteNameModal isOpen={isFavNameModalOpen} onClose={() => { window.history.back(); }} onSave={handleSaveFavorite} language={settings.language} />}
      {rippleEffect && <div key={rippleEffect.id} className="fixed z-[30] left-1/2 bottom-8 w-12 h-12 bg-cyan-400 rounded-full animate-ripple-screen pointer-events-none"></div>}
      {isProcessingImage && <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center"><div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl flex flex-col items-center animate-popIn"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div><p className="text-gray-800 dark:text-white font-bold">{t(settings.language, 'analyzing')}</p></div></div>}
      {duplicateItemAlert && <div className="fixed top-28 left-1/2 -translate-x-1/2 z-[100] animate-popIn"><div className="bg-amber-500 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-3 border border-amber-400/50"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg><span className="font-bold text-sm"><span className="capitalize">{duplicateItemAlert.name}</span> {t(settings.language, 'itemAlreadyExists')}</span></div></div>}
      {errorMessage && <div className="fixed top-28 left-1/2 -translate-x-1/2 z-[100] animate-popIn w-[90%] max-w-sm"><div className="bg-red-500 text-white px-4 py-3 rounded-xl shadow-xl flex items-start gap-3 border border-red-400/50 backdrop-blur-md"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span className="font-medium text-sm leading-tight break-words">{errorMessage}</span></div></div>}

      <div className="flex-1 relative overflow-hidden">
        <div className="absolute inset-0 flex transition-transform duration-300 ease-in-out" style={{ transform: `translateX(-${currentViewIndex * 100}%)` }}>
            <div className="w-full flex-shrink-0 h-full overflow-y-auto overscroll-y-none">
                <HomeView sessions={sessions} onRecordMeal={openAddMeal} onRecordSymptom={openAddSymptom} language={settings.language} analysisResult={analysisResult} onRunAnalysis={runAnalysis} isAnalyzing={isAnalyzing} smartSuggestions={mealSuggestions} onLogSuggestion={handleLogSuggestion} onOpenWater={openWater} onQuickAddWater={handleQuickAddWater} />
            </div>
            
            {/* Meal Journal View (Index 1) */}
            <div className="w-full flex-shrink-0 h-full flex flex-col bg-gray-50 dark:bg-gray-900 relative">
                <div className="px-2 py-2 bg-gray-50 dark:bg-gray-900 z-10 shrink-0 border-b border-gray-100 dark:border-gray-800 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.02)]">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 px-2">{t(settings.language, 'mealJournal')}</h2>
                        <button id="meal-calendar-toggle" onClick={() => setIsCalendarExpanded(!isCalendarExpanded)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2-0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 16h.01M14 16h.01" /></svg></button>
                    </div>
                    {!isCalendarExpanded && <WeeklyCalendar sessions={sessions} language={settings.language} density={settings.density} currentDate={selectedDate} onDayClick={handleCalendarSelect} />}
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 px-4 pt-2">
                    {isCalendarExpanded ? (
                        <MonthlyCalendar sessions={sessions} onSelectDate={handleCalendarSelect} selectedDate={selectedDate} type="meal" language={settings.language} />
                    ) : (
                        <div className="max-w-md mx-auto pb-4">
                            {getDailySessions(sessions, selectedDate).length === 0 ? <div className="text-center py-12 text-gray-400 dark:text-gray-600">{t(settings.language, 'noMeals')}</div> : (
                                <DaySummary key={selectedDate.toDateString()} date={selectedDate.toDateString()} subtitle={`${getDailySessions(sessions, selectedDate).length} ${t(settings.language, getDailySessions(sessions, selectedDate).length === 1 ? 'meal' : 'meals')}`} language={settings.language} defaultOpen={true}>
                                    {getDailySessions(sessions, selectedDate).map((session, sessionIdx) => (
                                        <FoodList 
                                            key={session.id} 
                                            session={session} 
                                            onAddMore={(id) => resumeSession(id, 'meal')} 
                                            onScan={(id) => { activeSessionIdRef.current = id; setActiveSessionId(id); setIsBarcodeOpen(true); }}
                                            onCamera={(id) => { activeSessionIdRef.current = id; setActiveSessionId(id); setIsCameraOpen(true); }}
                                            onDeleteItem={(itemId) => handleDeleteFoodItem(session.id, itemId)} 
                                            onDeleteSession={(sessionId) => handleDeleteMealSession(sessionId)} 
                                            isRecordingThisSession={status === 'connected' && activeSessionId === session.id && mode === 'meal'} 
                                            language={settings.language} 
                                            onUpdateSessionTime={(id, date) => handleUpdateSessionTime(id, date, 'meal')} 
                                            onUpdateItem={(itemId, q, u) => handleUpdateFoodItem(session.id, itemId, q, u)} 
                                            onUpdateRecipe={(itemId, updated, saveAsCustom) => handleUpdateRecipe(session.id, itemId, updated, saveAsCustom)} 
                                            onUpdateSessionType={(type) => handleUpdateSessionType(session.id, type)} 
                                            onManualAdd={(items) => handleManualAddFoods(session.id, items)} 
                                            onToggleStar={handleToggleStar} 
                                            isFirstSession={sessionIdx === 0} 
                                            isOpen={expandedSessionId === session.id} 
                                            onToggle={() => setExpandedSessionId(prev => prev === session.id ? null : session.id)} 
                                            density={settings.density} 
                                            customRecipes={customRecipes} 
                                            onMerge={(sourceId) => handleMergeSessions(sourceId, session.id, 'meal')} 
                                        />
                                    ))}
                                </DaySummary>
                            )}
                        </div>
                    )}
                </div>
                {!isCalendarExpanded && (
                    <div className={`flex-none bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-3 flex flex-col justify-end z-20`}>
                        {mealSuggestions.length > 0 && (
                            <div className="w-full">
                                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1 flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>{t(settings.language, 'smartSuggestions')}</h4>
                                <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                                    {mealSuggestions.map((suggestion, idx) => suggestion.type === 'favorite' ? (
                                        <button key={`fav-${idx}`} onClick={() => handleLogSuggestion(suggestion)} className="px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-full text-xs font-bold text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 hover:border-yellow-400 dark:hover:bg-yellow-900/40 transition-colors shadow-sm flex-shrink-0 flex items-center gap-1.5"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>{suggestion.label}</button>
                                    ) : (
                                        <button key={`item-${idx}`} onClick={() => handleLogSuggestion(suggestion)} className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 dark:hover:bg-emerald-900/30 dark:hover:text-emerald-300 transition-colors shadow-sm flex-shrink-0">{suggestion.label}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <button onClick={openAddMeal} className="flex items-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white py-2 pl-4 pr-6 rounded-full shadow-lg transition-transform active:scale-95 w-full justify-center"><div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg></div><span className="font-bold text-sm uppercase tracking-wide">{t(settings.language, 'recordMeal')}</span></button>
                    </div>
                )}
            </div>

            {/* Symptom Journal View (Index 2) */}
            <div className="w-full flex-shrink-0 h-full flex flex-col bg-gray-50 dark:bg-gray-900 relative">
                <div className="px-2 py-2 bg-gray-50 dark:bg-gray-900 z-10 shrink-0 border-b border-gray-100 dark:border-gray-800 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.02)]">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 px-2">{t(settings.language, 'symptomJournal')}</h2>
                        <button id="symptom-calendar-toggle" onClick={() => setIsCalendarExpanded(!isCalendarExpanded)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2-0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 16h.01M14 16h.01" /></svg></button>
                    </div>
                    {!isCalendarExpanded && <WeeklyCalendar sessions={symptomSessions} language={settings.language} density={settings.density} currentDate={selectedDate} onDayClick={handleCalendarSelect} type="symptom" />}
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 px-4 pt-2">
                    {isCalendarExpanded ? (
                        <MonthlyCalendar sessions={symptomSessions} onSelectDate={handleCalendarSelect} selectedDate={selectedDate} type="symptom" language={settings.language} />
                    ) : (
                        <div className="max-w-md mx-auto pb-4">
                            {getDailySessions(symptomSessions, selectedDate).length === 0 ? <div className="text-center py-12 text-gray-400 dark:text-gray-600">{t(settings.language, 'noSymptoms')}</div> : (
                                <DaySummary key={`sym-${selectedDate.toDateString()}`} date={selectedDate.toDateString()} subtitle={`${getDailySessions(symptomSessions, selectedDate).length} ${t(settings.language, getDailySessions(symptomSessions, selectedDate).length === 1 ? 'symptom' : 'symptoms')}`} language={settings.language} defaultOpen={true}>
                                    {getDailySessions(symptomSessions, selectedDate).map((session, sessionIdx) => (
                                        <SymptomList 
                                            key={session.id} 
                                            session={session} 
                                            onAddMore={(id) => resumeSession(id, 'symptom')} 
                                            onDeleteItem={(itemId) => handleDeleteSymptomItem(session.id, itemId)} 
                                            onDeleteSession={(sessionId) => handleDeleteSymptomSession(sessionId)} 
                                            isRecordingThisSession={status === 'connected' && activeSessionId === session.id && mode === 'symptom'} 
                                            language={settings.language} 
                                            onUpdateSessionTime={(id, date) => handleUpdateSessionTime(id, date, 'symptom')} 
                                            onUpdateItem={(itemId, val) => handleUpdateSymptomItem(session.id, itemId, val)} 
                                            onManualAdd={(items) => handleManualAddSymptoms(session.id, items)} 
                                            isFirstSession={sessionIdx === 0} 
                                            density={settings.density} 
                                            onMerge={(sourceId) => handleMergeSessions(sourceId, session.id, 'symptom')} 
                                        />
                                    ))}
                                </DaySummary>
                            )}
                        </div>
                    )}
                </div>
                {!isCalendarExpanded && (
                    <div className={`flex-none bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-3 flex flex-col justify-end z-20`}>
                        {symptomSuggestions.length > 0 && (
                            <div className="w-full">
                                <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1 flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>{t(settings.language, 'smartSuggestions')}</h4>
                                <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
                                    {symptomSuggestions.map((suggestion, idx) => (
                                        <button key={`sym-sugg-${idx}`} onClick={() => handleLogSuggestion(suggestion)} className="px-3 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700 dark:hover:bg-orange-900/30 dark:hover:text-orange-300 transition-colors shadow-sm flex-shrink-0">{suggestion.label}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <button onClick={openAddSymptom} className="flex items-center gap-3 bg-orange-500 hover:bg-orange-400 text-white py-2 pl-4 pr-6 rounded-full shadow-lg transition-transform active:scale-95 w-full justify-center"><div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg></div><span className="font-bold text-sm uppercase tracking-wide">{t(settings.language, 'recordSymptom')}</span></button>
                    </div>
                )}
            </div>

            {/* Stats View (Index 3) */}
            <div className="w-full flex-shrink-0 h-full overflow-y-auto overscroll-y-none bg-gray-50 dark:bg-gray-900 relative"><StatsView sessions={sessions} language={settings.language} /></div>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-40 group"><button onClick={handleWaterClick} className="w-12 h-12 bg-cyan-500 hover:bg-cyan-400 text-white shadow-[0_4px_12px_rgba(6,182,212,0.4)] flex items-center justify-center transition-transform active:scale-95 group-hover:scale-105 border-4 border-gray-50 dark:border-gray-900" style={{ borderRadius: '50% 50% 50% 0', transform: 'rotate(-45deg)' }} title={t(settings.language, 'addWater') + " (Double tap quick add)"}><div className="transform rotate-45 flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" /></svg></div></button></div>
      <div className="flex-none bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 pb-safe px-4 py-2 z-20 flex justify-between items-center relative">
          <button id="tour-nav-home" onClick={() => handleViewChange(0)} className={`p-2 flex flex-col items-center flex-1 ${currentViewIndex === 0 ? 'text-emerald-600 dark:text-emerald-500' : 'text-gray-400 dark:text-gray-600'}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg><span className="text-[10px] font-medium mt-1">{t(settings.language, 'home')}</span></button>
          <button id="tour-nav-meals" onClick={() => handleViewChange(1)} className={`p-2 flex flex-col items-center flex-1 ${currentViewIndex === 1 ? 'text-emerald-600 dark:text-emerald-500' : 'text-gray-400 dark:text-gray-600'}`}>
              <Utensils size={24} strokeWidth={currentViewIndex === 1 ? 2.5 : 2} />
              <span className="text-[10px] font-medium mt-1">{t(settings.language, 'meals')}</span>
          </button>
          <div className="w-12"></div>
          <button id="tour-nav-symptoms" onClick={() => handleViewChange(2)} className={`p-2 flex flex-col items-center flex-1 ${currentViewIndex === 2 ? 'text-emerald-600 dark:text-emerald-500' : 'text-gray-400 dark:text-gray-600'}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg><span className="text-[10px] font-medium mt-1">{t(settings.language, 'symptoms')}</span></button>
          <button id="tour-nav-stats" onClick={() => handleViewChange(3)} className={`p-2 flex flex-col items-center flex-1 ${currentViewIndex === 3 ? 'text-emerald-600 dark:text-emerald-500' : 'text-gray-400 dark:text-gray-600'}`}><svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2-0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><span className="text-[10px] font-medium mt-1">{t(settings.language, 'stats')}</span></button>
      </div>
      {(status === 'connected' || status === 'connecting') && <MicButton status={status} onClick={handleFabClick} volume={volume} language={settings.language} />}
    </div>
  );
};

export default App;
